import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";

export const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center">
    <span className="flex items-center rounded-xl bg-white px-3 py-1.5 shadow-lg shadow-black/25 transition-transform duration-300 hover:scale-[1.03]">
      <img src="/logo.png" alt="DG Hubs — Grow Your Business" className="h-9 w-auto" />
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-cyan-500/10 bg-dg-navy/85 backdrop-blur-xl py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-semibold tracking-wide transition-colors duration-300 ${
                    isActive ? "text-dg-cyan" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="header-consultation-btn"
              onClick={() => navigate("/contact")}
              className="group hidden items-center gap-2 rounded-full bg-dg-cyan px-5 py-2.5 text-[13px] font-bold text-dg-navy transition-all duration-300 hover:bg-white hover:shadow-[0_0_25px_rgba(0,184,212,0.4)] sm:inline-flex"
            >
              Get Free Consultation
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="grid h-10 w-10 place-items-center rounded-lg border border-white/10 text-white lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-dg-navy/97 backdrop-blur-2xl px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `font-display block py-2 text-3xl font-bold ${isActive ? "text-dg-cyan" : "text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                <button
                  data-testid="mobile-consultation-btn"
                  onClick={() => navigate("/contact")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-dg-cyan px-7 py-3.5 font-bold text-dg-navy"
                >
                  Get Free Consultation <ArrowUpRight size={16} />
                </button>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
